import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, CircularProgress, Snackbar, Alert, Menu, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../redux/store';
import { setUser } from '../../redux/authSlice';
import { cardSx } from '../../theme/sx';
import { getTheme } from '../../theme/themes';
import axios from '../../interceptors/axios';

interface ProfileData {
  first_name: string;
  last_name: string;
  email: string;
  login: string;
  isAdmin: boolean;
}

const LANGUAGES = [
  { code: 'en', label: 'English',     flag: '🇬🇧' },
  { code: 'ua', label: 'Українська',  flag: '🇺🇦' },
];

/* ── Reusable field ──────────────────────────────────────── */
const Field: React.FC<{
  label: string;
  value: string;
  onChange?: (v: string) => void;
  type?: string;
  placeholder?: string;
  readOnly?: boolean;
}> = ({ label, value, onChange, type = 'text', placeholder, readOnly }) => (
  <Box>
    <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-disabled)', mb: '8px' }}>
      {label}
    </Typography>
    <Box
      component="input"
      type={type}
      value={value}
      onChange={e => onChange?.((e.target as HTMLInputElement).value)}
      placeholder={placeholder || ''}
      readOnly={readOnly}
      sx={{
        width: '100%', boxSizing: 'border-box',
        px: '12px', py: '9px', borderRadius: '8px',
        border: '1px solid var(--glass-border)',
        background: 'var(--input-bg, var(--sidebar-bg))',
        color: readOnly ? 'var(--text-disabled)' : 'var(--text-primary)',
        fontSize: 13, fontFamily: 'inherit',
        outline: 'none', transition: 'border-color .15s',
        cursor: readOnly ? 'default' : 'text',
        WebkitBoxShadow: '0 0 0 1000px var(--input-bg, var(--sidebar-bg)) inset',
        WebkitTextFillColor: readOnly ? 'var(--text-disabled)' : 'var(--text-primary)',
        '&:focus': readOnly ? {} : { borderColor: 'var(--accent)' },
      } as any}
    />
  </Box>
);

/* ── Save button ─────────────────────────────────────────── */
const SaveBtn: React.FC<{ onClick: () => void; loading: boolean; label: string; disabled?: boolean }> = ({ onClick, loading, label, disabled }) => (
  <Box
    onClick={!loading && !disabled ? onClick : undefined}
    sx={{
      display: 'inline-flex', alignItems: 'center', gap: '8px',
      px: '18px', py: '8px', borderRadius: '8px',
      background: disabled ? 'var(--glass-hover)' : 'var(--accent)',
      color: disabled ? 'var(--text-disabled)' : '#fff',
      fontSize: 13, fontWeight: 600,
      cursor: loading || disabled ? 'default' : 'pointer',
      userSelect: 'none', transition: 'all .15s',
      '&:hover': !loading && !disabled ? { filter: 'brightness(1.1)' } : {},
    }}
  >
    {loading && <CircularProgress size={13} sx={{ color: '#fff' }} />}
    {label}
  </Box>
);

/* ── Section title — identical to Settings page ──────────── */
const SectionTitle: React.FC<{ title: string }> = ({ title }) => (
  <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.06em', mb: '10px' }}>
    {title}
  </Typography>
);

/* ── Main component ──────────────────────────────────────── */
const ProfilePage: React.FC<{ themeId: string; onThemeChange: (id: string) => void }> = ({ themeId, onThemeChange }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const reduxUser = useSelector((s: RootState) => s.auth.user);

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<ProfileData>({ first_name: '', last_name: '', email: '', login: '', isAdmin: false });
  const [dirty, setDirty]   = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast]   = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  const [pwdCurrent, setPwdCurrent] = useState('');
  const [pwdNew, setPwdNew]         = useState('');
  const [pwdConfirm, setPwdConfirm] = useState('');
  const [pwdSaving, setPwdSaving]   = useState(false);
  const [langAnchor, setLangAnchor] = useState<null | HTMLElement>(null);

  const isDark = getTheme(themeId).mode === 'dark';

  const fetchProfile = useCallback(async () => {
    try {
      const { data } = await axios.get<ProfileData>('/auth/me');
      setProfile(data);
    } catch {
      setToast({ msg: 'Failed to load profile', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const handleField = (key: keyof ProfileData, value: string) => {
    setProfile(p => ({ ...p, [key]: value }));
    setDirty(true);
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await axios.patch('/auth/profile', { first_name: profile.first_name, last_name: profile.last_name });
      dispatch(setUser({ ...reduxUser!, login: profile.login }));
      setDirty(false);
      setToast({ msg: t('profile.saved'), type: 'success' });
    } catch {
      setToast({ msg: t('profile.saveError'), type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async () => {
    if (pwdNew !== pwdConfirm) { setToast({ msg: t('profile.pwdMismatch'), type: 'error' }); return; }
    if (pwdNew.length < 4)     { setToast({ msg: t('profile.pwdTooShort'),  type: 'error' }); return; }
    setPwdSaving(true);
    try {
      await axios.post('/auth/change-password', { currentPassword: pwdCurrent, newPassword: pwdNew });
      setPwdCurrent(''); setPwdNew(''); setPwdConfirm('');
      setToast({ msg: t('profile.pwdChanged'), type: 'success' });
    } catch (err: any) {
      setToast({ msg: err?.response?.data?.message || t('profile.pwdError'), type: 'error' });
    } finally {
      setPwdSaving(false);
    }
  };

  const switchLang = (lang: string) => { i18n.changeLanguage(lang); localStorage.setItem('pv-lang', lang); };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <CircularProgress size={32} sx={{ color: 'var(--accent)' }} />
      </Box>
    );
  }

  const displayName = profile.first_name || profile.last_name
    ? `${profile.first_name} ${profile.last_name}`.trim()
    : profile.login;

  return (
    <Box sx={{ flex: 1, overflowY: 'auto', p: '16px' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

      {/* ── Section 1: Personal info ── */}
      <Box>
        <SectionTitle title={t('profile.personal')} />
        <Box sx={{ ...cardSx, p: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {/* User header inside card */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', pb: '4px' }}>
            <Box sx={{
              width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
              background: 'linear-gradient(135deg, var(--accent), var(--accent-light))',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18, fontWeight: 700, color: '#fff',
            }}>
              {displayName[0]?.toUpperCase() || 'A'}
            </Box>
            <Box>
              <Typography sx={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.2 }}>{displayName}</Typography>
              <Typography sx={{ fontSize: 12, color: 'var(--text-disabled)', mt: '2px' }}>
                {profile.isAdmin ? t('user.admin') : t('user.operator')}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ height: '1px', background: 'var(--glass-border)' }} />

          <Box sx={{ maxWidth: 480, display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Field label={t('profile.firstName')} value={profile.first_name} onChange={v => handleField('first_name', v)} placeholder={t('profile.firstName')} />
            <Field label={t('profile.lastName')}  value={profile.last_name}  onChange={v => handleField('last_name',  v)} placeholder={t('profile.lastName')} />
            <Field label={t('profile.login')} value={profile.login} readOnly />
          </Box>

          {dirty && (
            <Box>
              <SaveBtn onClick={saveProfile} loading={saving} label={saving ? t('profile.saving') : t('profile.save')} />
            </Box>
          )}
        </Box>
      </Box>

      {/* ── Section 2: Change password ── */}
      <Box>
        <SectionTitle title={t('profile.changePassword')} />
        <Box sx={{ ...cardSx, p: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Box sx={{ maxWidth: 480, display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Field label={t('profile.currentPassword')} value={pwdCurrent} onChange={setPwdCurrent} type="password" placeholder="••••••••" />
            <Field label={t('profile.newPassword')}     value={pwdNew}     onChange={setPwdNew}     type="password" placeholder="••••••••" />
            <Field label={t('profile.confirmPassword')} value={pwdConfirm} onChange={setPwdConfirm} type="password" placeholder="••••••••" />
          </Box>
          <Box>
            <SaveBtn
              onClick={changePassword} loading={pwdSaving}
              label={pwdSaving ? t('profile.saving') : t('profile.changePasswordBtn')}
              disabled={!pwdCurrent || !pwdNew || !pwdConfirm}
            />
          </Box>
        </Box>
      </Box>

      {/* ── Section 3: Appearance ── */}
      <Box>
        <SectionTitle title={t('profile.appearance')} />
        <Box sx={{ ...cardSx, p: '20px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* Language — dropdown */}
          <Box>
            <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-disabled)', mb: '8px' }}>
              {t('profile.language')}
            </Typography>
            <Box
              onClick={e => setLangAnchor(e.currentTarget)}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: '10px',
                px: '13px', py: '9px', borderRadius: '9px', cursor: 'pointer',
                border: '1px solid var(--glass-border)',
                background: 'var(--input-bg, var(--sidebar-bg))',
                minWidth: 200, transition: 'border-color .15s',
                '&:hover': { borderColor: 'var(--accent)' },
              }}
            >
              <Typography sx={{ fontSize: 16, lineHeight: 1 }}>
                {LANGUAGES.find(l => l.code === i18n.language)?.flag || '🇬🇧'}
              </Typography>
              <Typography sx={{ fontSize: 14, color: 'var(--text-primary)', flex: 1 }}>
                {LANGUAGES.find(l => l.code === i18n.language)?.label || 'English'}
              </Typography>
              <Typography sx={{ fontSize: 10, color: 'var(--text-disabled)' }}>▾</Typography>
            </Box>
            <Menu
              anchorEl={langAnchor}
              open={Boolean(langAnchor)}
              onClose={() => setLangAnchor(null)}
              slotProps={{ paper: { sx: { minWidth: 200, borderRadius: '10px', border: '1px solid var(--glass-border)', background: 'var(--glass-bg)', backdropFilter: 'blur(16px)' } } }}
            >
              {LANGUAGES.map(l => (
                <MenuItem
                  key={l.code}
                  selected={i18n.language === l.code}
                  onClick={() => { switchLang(l.code); setLangAnchor(null); }}
                  sx={{ fontSize: 14, gap: '10px', color: 'var(--text-secondary)' }}
                >
                  {l.flag} {l.label}
                </MenuItem>
              ))}
            </Menu>
          </Box>

          <Box sx={{ height: '1px', background: 'var(--glass-border)' }} />

          {/* Theme */}
          <Box>
            <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-disabled)', mb: '10px' }}>
              {t('profile.theme')}
            </Typography>
            <Box sx={{ display: 'flex', gap: '8px' }}>
              {[
                { id: 'navy', label: t('profile.dark'),  icon: '🌙' },
                { id: 'sky',  label: t('profile.light'), icon: '☀️' },
              ].map(m => {
                const active = themeId === m.id;
                return (
                  <Box key={m.id} onClick={() => onThemeChange(m.id)} sx={{
                    flex: 1, py: '10px', borderRadius: '9px', textAlign: 'center', cursor: 'pointer',
                    border: '1px solid', borderColor: active ? 'var(--accent)' : 'var(--glass-border)',
                    background: active ? 'rgba(99,102,241,.12)' : 'transparent',
                    transition: 'all .15s',
                    '&:hover': { borderColor: 'var(--accent)', background: 'var(--glass-hover)' },
                  }}>
                    <Typography sx={{ fontSize: 20, lineHeight: 1.2 }}>{m.icon}</Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 500, mt: '4px', color: active ? 'var(--accent-light)' : 'var(--text-secondary)' }}>
                      {m.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>

        </Box>
      </Box>

      </Box>{/* end maxWidth wrapper */}

      <Snackbar open={Boolean(toast)} autoHideDuration={3000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast?.type || 'success'} onClose={() => setToast(null)} sx={{ borderRadius: '10px' }}>
          {toast?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProfilePage;
